import { AfterViewInit, Component, ElementRef, Input, OnDestroy, ViewChild } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { faCheck, faCrosshairs } from '@fortawesome/free-solid-svg-icons';
import { ProctorService } from '../services/proctor.service';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-calibration-modal',
  templateUrl: './calibration-modal.component.html',
  styleUrls: ['./calibration-modal.component.css']
})
export class CalibrationModalComponent implements AfterViewInit, OnDestroy {
  faCrosshairs = faCrosshairs;
  faCheck = faCheck;
  @Input() examId: number;
  @Input() scheduleId: number;

  calibrationPoints: CalibrationPoint[] = [
    { id: 1, name: 'Top Left', x: '5%', y: '5%' },
    { id: 2, name: 'Top Right', x: '95%', y: '5%' },
    { id: 3, name: 'Bottom Left', x: '5%', y: '95%' },
    { id: 4, name: 'Bottom Right', x: '95%', y: '95%' },
    // Sit just below the centered status text so it stays clickable
    { id: 5, name: 'Center', x: '50%', y: 'calc(50% + 2.5rem)' }
  ];

  currentIndex = 0;
  recording = false;
  confirmed = false;
  finishing = false;
  cameraReady = false;
  private readonly checkAnimationMs = 700;
  private stream: MediaStream | undefined;
  private captures: CalibrationCapture[] = [];
  private failed = false;

  @ViewChild('video') videoRef: ElementRef<HTMLVideoElement>;
  @ViewChild('canvas') canvasRef: ElementRef<HTMLCanvasElement>;

  constructor(
    public activeModal: NgbActiveModal,
    private proctorService: ProctorService,
    private toastr: ToastrService
  ) { }

  async ngAfterViewInit() {
    await this.startCamera();
  }

  ngOnDestroy(): void {
    this.detachCamera();
  }

  async captureCurrentPoint() {
    if (!this.cameraReady || this.recording || this.confirmed || this.finishing || this.failed) {
      return;
    }

    this.recording = true;
    try {
      const frame = await this.captureFrame();

      this.captures.push({
        point: this.currentIndex + 1,
        name: this.calibrationPoints[this.currentIndex].name.replace(' ', ''),
        frame
      });

      this.confirmed = true;
      await this.delay(this.checkAnimationMs);
      await this.nextPoint();
    } catch (err) {
      this.failCalibration('Failed to capture calibration frame.', err);
    }
  }

  private captureFrame(): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const video = this.videoRef?.nativeElement;
      const canvas = this.canvasRef?.nativeElement;

      if (!video || !canvas) {
        reject(new Error('Video or canvas not ready'));
        return;
      }

      if (!video.videoWidth || !video.videoHeight) {
        reject(new Error('Webcam is not ready'));
        return;
      }

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Unable to get canvas context'));
        return;
      }

      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      canvas.toBlob(blob => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to capture image'));
        }
      }, 'image/jpeg', 1);
    });
  }

  private async nextPoint() {
    if (this.currentIndex < this.calibrationPoints.length - 1) {
      this.recording = false;
      this.confirmed = false;
      this.currentIndex++;
      return;
    }

    // Keep the last target disabled while uploading so a second click cannot start another capture.
    this.finishing = true;
    this.recording = true;
    this.confirmed = true;
    await this.finishCalibration();
  }

  private async finishCalibration() {
    try {
      const formData = new FormData();
      formData.append('examId', this.examId.toString());
      formData.append('scheduleId', this.scheduleId.toString());

      this.captures.forEach((capture, captureIndex) => {
        formData.append(`captures[${captureIndex}].point`, capture.point.toString());
        formData.append(`captures[${captureIndex}].name`, capture.name);
        formData.append(`captures[${captureIndex}].frame`, capture.frame, `${capture.name}.jpg`);
      });

      await this.proctorService.uploadCalibration(formData);
      this.detachCamera();
      this.activeModal.close(true);
    } catch (err) {
      this.failCalibration('Failed to upload calibration data.', err);
    }
  }

  private async startCamera() {
    try {
      // Reuse the shared proctee stream so later recording/facial recognition use the same camera.
      this.stream = await this.proctorService.getVideoStream(this.proctorService.allowExternalWebcam);
      const video = this.videoRef.nativeElement;
      // Attach video tracks only and keep the element muted so shared mic audio is never heard.
      video.srcObject = new MediaStream(this.stream.getVideoTracks());
      video.muted = true;
      video.volume = 0;
      await video.play();
      await this.waitForVideoReady(video);
      this.cameraReady = true;
    } catch (err) {
      this.failCalibration('Unable to access webcam for calibration.', err);
    }
  }

  private waitForVideoReady(video: HTMLVideoElement): Promise<void> {
    return new Promise((resolve, reject) => {
      const timeoutMs = 30000;
      const timeoutId = window.setTimeout(() => {
        cleanup();
        reject(new Error('Timed out waiting for webcam'));
      }, timeoutMs);

      const onReady = () => {
        if (video.videoWidth > 0 && video.videoHeight > 0) {
          cleanup();
          resolve();
        }
      };

      const cleanup = () => {
        window.clearTimeout(timeoutId);
        video.removeEventListener('loadedmetadata', onReady);
        video.removeEventListener('loadeddata', onReady);
        video.removeEventListener('playing', onReady);
      };

      video.addEventListener('loadedmetadata', onReady);
      video.addEventListener('loadeddata', onReady);
      video.addEventListener('playing', onReady);
      onReady();
    });
  }

  private failCalibration(userMessage: string, err?: unknown) {
    if (this.failed) {
      return;
    }
    this.failed = true;
    console.error(err ?? userMessage);
    this.toastr.error(userMessage);
    this.detachCamera();
    this.activeModal.dismiss(new Error(userMessage));
  }

  private detachCamera() {
    const video = this.videoRef?.nativeElement;
    if (video) {
      video.srcObject = null;
    }
    // Do not stop tracks — the stream is owned/cached by ProctorService for later use.
    this.stream = undefined;
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

interface CalibrationPoint {
  id: number;
  name: string;
  x: string;
  y: string;
}

interface CalibrationCapture {
  point: number;
  name: string;
  frame: Blob;
}
